import { useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import { mdiCheck } from '@mdi/js'
import Icon from '@mdi/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { RadioTiles } from 'components/RadioTiles'
import theme from 'theme_'
import { softwareTilesValues } from '../const'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { TableLoader } from 'components/Table'
import HomeAvailableSoftwareUpdatesTable from '../HomeAvailableSoftwareUpdatesTable'

import {
  homeAvailableSoftwareSelector,
  homeConfigSelectors,
  selectedHomeSelector,
} from 'features/home/selectors'
import {
  HomeSoftwareVersionContainer,
  StyledSoftwareUpdatesHeader,
  StyledSoftwareUpdatesInfo,
} from './styles'
import { HomeSoftwareTitle, HomeSoftwareEmptyBlock } from '../styles'
import SomethingWentWrongBlock from 'components/SomethingWentWrongBlock'
import {
  usePreviousSoftwareVersions,
  useSoftwareUpdates,
  useValidSoftware,
} from '../utils'
import {
  fetchHomeAvailableSoftwareThunk,
  homeAvailableSoftwareActions,
} from '../slice'
import { getHomeConfigThunk } from '../../HomeConfiguration/slice'
import { homeSoftwareMessages } from './messages'

function HomeSoftwareUpdates() {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()

  const [availableUpdatesTile, setAvailableUpdatesTile] = useState(
    softwareTilesValues.availableUpdates
  )

  const softwareUpdatesTiles = [
    {
      label: formatMessage(homeSoftwareMessages.available),
      value: softwareTilesValues.availableUpdates,
      defaultChecked: true,
    },
    {
      label: formatMessage(homeSoftwareMessages.previous),
      value: softwareTilesValues.previous,
    },
  ]

  const selectedHome = useAppSelector(selectedHomeSelector.data)
  const hostConfig = useAppSelector(homeConfigSelectors.data)
  const availableSoftware = useAppSelector(homeAvailableSoftwareSelector.data)

  const validSoftware = useValidSoftware(
    hostConfig?.hostMachineType || 0,
    selectedHome!,
    availableSoftware!
  )

  const availableUpdates = useSoftwareUpdates(selectedHome, validSoftware)
  const previousAvailableSoftware = usePreviousSoftwareVersions(
    selectedHome,
    validSoftware
  )

  const selectedTableData = useMemo(
    () =>
      availableUpdatesTile === softwareTilesValues.availableUpdates
        ? availableUpdates
        : previousAvailableSoftware,
    [availableUpdatesTile, previousAvailableSoftware, availableUpdates]
  )

  const isAvailableSoftwareFetching = useAppSelector(
    homeAvailableSoftwareSelector.isFetching
  )
  const isAvailableSoftwareLoaded = useAppSelector(
    homeAvailableSoftwareSelector.isFinished
  )
  const hasAvailableSoftwareError = useAppSelector(
    homeAvailableSoftwareSelector.error
  )

  useEffect(() => {
    if (selectedHome?.id) {
      dispatch(
        fetchHomeAvailableSoftwareThunk(selectedHome.id, { location: true })
      )
      dispatch(getHomeConfigThunk(selectedHome.id))
    }
  }, [dispatch, selectedHome?.id])

  useEffect(() => {
    return () => {
      dispatch(homeAvailableSoftwareActions.clear())
    }
  }, [dispatch])

  const shouldEmptyMessageBeShown =
    !isAvailableSoftwareFetching &&
    isAvailableSoftwareLoaded &&
    !hasAvailableSoftwareError

  return (
    <>
      <StyledSoftwareUpdatesHeader>
        <StyledSoftwareUpdatesInfo>
          <HomeSoftwareTitle>
            <FormattedMessage {...homeSoftwareMessages.available} />
          </HomeSoftwareTitle>
          <RadioTiles
            options={softwareUpdatesTiles}
            name="updateTileFilter"
            onChange={setAvailableUpdatesTile}
          />
        </StyledSoftwareUpdatesInfo>
      </StyledSoftwareUpdatesHeader>

      {isAvailableSoftwareFetching && <TableLoader />}

      {hasAvailableSoftwareError && <SomethingWentWrongBlock />}

      {shouldEmptyMessageBeShown && (
        <>
          {isEmpty(selectedTableData) ? (
            <HomeSoftwareEmptyBlock>
              {availableUpdatesTile === softwareTilesValues.availableUpdates ? (
                <HomeSoftwareVersionContainer>
                  <Icon
                    path={mdiCheck}
                    size={2}
                    color={theme.colors.successGreen}
                  />
                  <FormattedMessage {...homeSoftwareMessages.haveLatest} />
                </HomeSoftwareVersionContainer>
              ) : (
                <HomeSoftwareVersionContainer>
                  <Icon
                    path={mdiCheck}
                    size={2}
                    color={theme.colors.successGreen}
                  />
                  <FormattedMessage {...homeSoftwareMessages.noEarlier} />
                </HomeSoftwareVersionContainer>
              )}
            </HomeSoftwareEmptyBlock>
          ) : (
            <HomeAvailableSoftwareUpdatesTable data={selectedTableData} />
          )}
        </>
      )}
    </>
  )
}

export default HomeSoftwareUpdates
